import * as fromUserActions from '../actions/user.actions';
import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AuthService} from '../../services/api/auth.service';
import {catchError, map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';
import {UserRestService} from '../../services/api/user.rest-service';
import {Adress} from '../../models/adress.model';
import {UserResponse} from '../../models/user-response.model';
import {VaccineRestService} from '../../services/api/vaccination-rest-service';
import {FluShopService} from '../../services/flu-shop.service';

@Injectable()
export class UserEffects {
    private actions$ = inject(Actions);
    private authService = inject(AuthService);
    private userRestService = inject(UserRestService);
    private vaccineRestService = inject(VaccineRestService);
    private fluShopService = inject(FluShopService);

    /**
     * On LoadUserAction request User from gateway and store it via LoadUserCompleteAction
     * or catch the error via LoadUserFailAction
     *
     * @type {Observable<any[]>}
     */
    loadUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadUserAction>(
                fromUserActions.UserActionTypes.LOAD_USER
            ),
            map(action => action),
            switchMap(() =>
                this.authService.getUserResponseFromGateway().pipe(
                    switchMap(data => [
                        new fromUserActions.LoadUserCompleteAction(new UserResponse(data.returnObject)),
                        new fromUserActions.LoadDashAction(),
                        new fromUserActions.LoadNavAction()
                    ]),
                    catchError(error => of(new fromUserActions.LoadUserFailAction(error)))
                )
            )
        )
    );

    loadDash$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadDashAction>(fromUserActions.UserActionTypes.LOAD_DASH),
            switchMap(() =>
                this.vaccineRestService.getVaccineShopConfig().pipe(
                    switchMap(conf =>
                        this.fluShopService.loadActivePreorderCategories().pipe(
                            switchMap(fluShopTiles =>
                                this.userRestService.getStaticNav(this.userRestService.CMS_URL).pipe(
                                    catchError(error => {
                                        console.error('getStaticNav failed', error);
                                        return of({});
                                    }),
                                    map(data => new fromUserActions.LoadDashCompleteAction({
                                        data: this.getObjectValues(data),
                                        url: this.userRestService.CMS_URL,
                                        vaccineConfig: conf.returnObject,
                                        fluShopTiles: fluShopTiles,
                                        CDN: this.userRestService.CDN
                                    }))
                                )
                            )
                        )
                    )
                )
            )
        )
    );

    loadNav$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadNavAction>(fromUserActions.UserActionTypes.LOAD_NAV),
            switchMap(() =>
                this.vaccineRestService.getVaccineShopConfig().pipe(
                    switchMap(conf =>
                        this.fluShopService.loadActivePreorderCategories().pipe(
                            switchMap(fluShopTiles =>
                                this.userRestService.getStaticNav(this.userRestService.CMS_URL).pipe(
                                    catchError(error => {
                                        // Handle the error and provide a fallback value
                                        console.error('getStaticNav failed', error);
                                        return of({}); // Fallback to an empty object or any default value
                                    }),
                                    map(data => new fromUserActions.LoadNavCompleteAction({
                                        data: this.getObjectValues(data),
                                        url: this.userRestService.CMS_URL,
                                        vaccineConfig: conf.returnObject,
                                        fluShopTiles: fluShopTiles,
                                        CDN: this.userRestService.CDN,
                                    }))
                                )
                            )
                        )
                    )
                )
            )
        )
    );

    loadIDFs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadUserIDFsAction>(
                fromUserActions.UserActionTypes.LOAD_USER_IDFS
            ),
            map(action => action.payload),
            switchMap(context =>
                this.userRestService.getIDFs(context).pipe(
                    map(data =>
                        new fromUserActions.LoadUserIDFsCompleteAction({
                            idfs: data.returnObject,
                            context: context
                        })
                    ),
                    catchError(error => of(new fromUserActions.LoadUserIDFsFailAction(error)))
                )
            )
        )
    );

    loadMainIdf$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadUserMainIdfAction>(
                fromUserActions.UserActionTypes.LOAD_USER_MAIN_IDF
            ),
            map(action => action),
            switchMap(() =>
                this.userRestService.getMainIdf().pipe(
                    map(data => new fromUserActions.LoadUserMainIdfCompleteAction(data.returnObject)),
                    catchError(error => of(new fromUserActions.LoadUserMainIdfFailAction(error)))
                )
            )
        )
    );

    loadVZs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadUserVZsAction>(
                fromUserActions.UserActionTypes.LOAD_USER_VZS
            ),
            map(action => action.payload),
            switchMap(idf =>
                this.userRestService.getVZs(idf).pipe(
                    map(data => new fromUserActions.LoadUserVZsCompleteAction(data.returnObject, idf)),
                    catchError(error => of(new fromUserActions.LoadUserVZsFailAction(error)))
                )
            )
        )
    );

    loadShippingAdresses$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadUserShippingAdressesAction>(
                fromUserActions.UserActionTypes.LOAD_USER_SA
            ),
            map(action => action),
            switchMap(() =>
                this.userRestService.getShippingAdresses().pipe(
                    map(data => {
                        const adresses = data.returnObject as Object[];
                        return new fromUserActions.LoadUserShippingAdressesCompleteAction(
                            adresses.map(adress => new Adress(adress))
                        );
                    }),
                    catchError(error => of(new fromUserActions.LoadUserShippingAdressesFailAction(error)))
                )
            )
        )
    );

    loadMainDistributionCenterAdress$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.GetMainDistributionCentersAdressAction>(
                fromUserActions.UserActionTypes.LOAD_USER_DISTRIBUTION_CENTER_ADRESS
            ),
            map(action => action),
            switchMap(() =>
                this.userRestService.getMainDistributionCentersAdress().pipe(
                    map(data => new fromUserActions.GetMainDistributionCentersAdressCompleteAction(data.returnObject)),
                    catchError(error => of(new fromUserActions.GetMainDistributionCentersAdressFailAction(error)))
                )
            )
        )
    );

    loadAllDistributionCenterAdress$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.GetAllDistributionCentersAdressAction>(
                fromUserActions.UserActionTypes.LOAD_ALL_USER_DISTRIBUTION_CENTER_ADRESS
            ),
            map(action => action),
            switchMap(() =>
                this.userRestService.getAllDistributionCentersAdress().pipe(
                    map(data => new fromUserActions.GetAllDistributionCentersAdressCompleteAction(data.returnObject)),
                    catchError(error => of(new fromUserActions.GetAllDistributionCentersAdressFailAction(error)))
                )
            )
        )
    );

    loadAuthToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadAuthTokenAction>(
                fromUserActions.UserActionTypes.LOAD_AUTH_TOKEN
            ),
            map(action => action),
            switchMap(() =>
                this.userRestService.getAuthToken().pipe(
                    map(data => new fromUserActions.LoadAuthTokenCompleteAction(data.returnObject)),
                    catchError(error => of(new fromUserActions.LoadAuthTokenFailAction(error)))
                )
            )
        )
    );

    loadAuthTokens$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromUserActions.LoadAuthTokensAction>(
                fromUserActions.UserActionTypes.LOAD_AUTH_TOKENS
            ),
            map(action => action),
            switchMap(() =>
                this.userRestService.getAuthTokens().pipe(
                    map(data => new fromUserActions.LoadAuthTokensCompleteAction(data.returnObject)),
                    catchError(error => of(new fromUserActions.LoadAuthTokensFailAction(error)))
                )
            )
        )
    );


    getObjectValues(obj) {
        const values = Object.keys(obj).map(e => obj[e]);
        return values;
    }
}